import {Injectable, inject} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable, map} from 'rxjs';
import {environment} from '@environment/environment';

export type RagStatus = 'not_analyzed' | 'pending' | 'analyzed' | 'error' | 'excluded' | 'deleting';

export interface DocItem {
  id: number;
  uuid: string;
  name: string;
  type: 'file' | 'folder';
  parent: number | null;
  dimension_file: number;
  original_filename: string | null;
  created_at: string;
  rag_status: RagStatus;
  rag_error: string | null;
}

type Envelope<T> = { success: boolean; data: T };

/** Cartelle e documenti dell'azienda per il back office (senza retry automatico sulle scritture). */
@Injectable({providedIn: 'root'})
export class DocumentsAdminApiService {
  private http = inject(HttpClient);
  private url = `${environment.api.base_url}/filemanager`;

  list(parent: number | null): Observable<DocItem[]> {
    const params = new HttpParams().set('parent', parent === null ? 'null' : parent).set('ordering', 'name');
    return this.http.get<Envelope<DocItem[]>>(`${this.url}/files`, {params}).pipe(map((r) => r.data));
  }

  folders(): Observable<DocItem[]> {
    const params = new HttpParams().set('type', 'folder').set('ordering', 'name');
    return this.http.get<Envelope<DocItem[]>>(`${this.url}/files`, {params}).pipe(map((r) => r.data));
  }

  createFolder(name: string, parent: number | null): Observable<DocItem> {
    return this.http.post<Envelope<DocItem>>(`${this.url}/files`, {name, type: 'folder', parent}).pipe(map((r) => r.data));
  }

  uploadPdf(name: string, parent: number | null, fileBase64: string): Observable<DocItem> {
    return this.http.post<Envelope<DocItem>>(`${this.url}/files`, {name, type: 'file', parent, file_base64: fileBase64})
      .pipe(map((r) => r.data));
  }

  update(id: number, body: {name?: string; parent?: number | null}): Observable<DocItem> {
    return this.http.patch<Envelope<DocItem>>(`${this.url}/files/${id}`, body).pipe(map((r) => r.data));
  }

  delete(id: number): Observable<unknown> {
    return this.http.delete(`${this.url}/files/${id}`);
  }

  /** Rende il documento disponibile all'AI (indicizzazione in coda). */
  enableAi(uuid: string): Observable<unknown> {
    return this.http.post(`${this.url}/files/${uuid}/rag/`, {});
  }

  /** Esclude il documento dall'AI: i suoi contenuti escono dall'indice, il PDF resta. */
  disableAi(uuid: string): Observable<unknown> {
    return this.http.delete(`${this.url}/files/${uuid}/rag/`);
  }

  pdf(uuid: string): Observable<Blob> {
    return this.http.get(`${this.url}/image/${uuid}`, {responseType: 'blob'});
  }
}
